/**
 * WorkspaceReceiptPane — Read-only receipt and verification inspector
 *
 * Surfaces receipt entries, verification status, hash references,
 * provenance linkage, and artifact refs.
 *
 * Gap extension v1: net-new pane. No existing GovernanceWorkspaceShell pane
 * surfaces receipts as a dedicated first-class concept. The InspectorPane
 * shows receipt detail on a selected event but does not provide a receipt
 * index or cross-receipt hash verification summary.
 *
 * SAFETY: Read-only, proof-only. No execution authority, no mutation.
 * Receipt display does not imply re-execution or re-verification authority.
 */

import React from 'react';
import type { ReceiptPaneModel, ReceiptEntry } from '../../modules/governanceEventStream/operationalRendering/types.js';
import { NEUTRAL, SPACING, TYPOGRAPHY, SEMANTIC } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface WorkspaceReceiptPaneProps {
  model: ReceiptPaneModel;
  onSelectReceipt?: (receiptId: string) => void;
}

function verificationColor(status: string) {
  if (status === 'VERIFIED') return SEMANTIC.stabilized;
  if (status === 'HASH_MISMATCH') return SEMANTIC.refusal;
  if (status === 'MISSING') return SEMANTIC.refusal;
  return SEMANTIC.neutral;
}

function verificationIcon(status: string): string {
  if (status === 'VERIFIED') return '✓';
  if (status === 'HASH_MISMATCH') return '✗';
  if (status === 'MISSING') return '—';
  return '?';
}

function ReceiptRow({ entry, onSelect }: { entry: ReceiptEntry; onSelect?: (id: string) => void }) {
  const color = verificationColor(entry.verificationStatus);
  return (
    <div
      onClick={() => onSelect?.(entry.receiptId)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: SPACING.sp2,
        padding: `${SPACING.sp2}px`,
        backgroundColor: entry.isSelected ? NEUTRAL.bg500 : NEUTRAL.bg700,
        borderRadius: 2,
        borderLeft: `2px solid ${entry.isSelected ? color.border : 'transparent'}`,
        cursor: onSelect ? 'pointer' : 'default',
        marginBottom: SPACING.sp1,
      }}
      data-testid={`receipt-entry-${entry.receiptId}`}
    >
      {/* Verification icon */}
      <span style={{
        fontSize: TYPOGRAPHY.label.size,
        fontWeight: 700,
        color: color.text,
        flexShrink: 0,
        minWidth: 14,
        textAlign: 'center' as const,
        lineHeight: 1.4,
      }}>
        {verificationIcon(entry.verificationStatus)}
      </span>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: SPACING.sp2 }}>
          {/* Receipt ID */}
          <span style={{
            fontSize: TYPOGRAPHY.caption.size,
            fontFamily: TYPOGRAPHY.mono.family,
            fontWeight: 600,
            color: NEUTRAL.text500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {entry.receiptId}
          </span>
          {/* Status chip */}
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            fontWeight: 600,
            color: color.text,
            backgroundColor: color.chip,
            padding: '1px 5px',
            borderRadius: 2,
            textTransform: 'uppercase' as const,
            letterSpacing: 0.3,
            flexShrink: 0,
          }}>
            {entry.verificationStatus.replace('_', ' ')}
          </span>
        </div>

        {/* Hash */}
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontFamily: TYPOGRAPHY.mono.family,
          color: NEUTRAL.text300,
          marginTop: 2,
        }}>
          hash: {entry.receiptHash.slice(0, 16)}…
        </div>

        {/* Meta row */}
        <div style={{ display: 'flex', gap: SPACING.sp3, marginTop: SPACING.sp1, flexWrap: 'wrap' }}>
          <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
            {entry.receiptType}
          </span>
          <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
            {entry.timestamp.slice(0, 19).replace('T', ' ')}
          </span>
          {entry.proofOnly && (
            <span style={{
              fontSize: TYPOGRAPHY.micro.size,
              color: NEUTRAL.text200,
              padding: '1px 4px',
              borderRadius: 2,
              backgroundColor: NEUTRAL.bg400,
            }}>
              restricted
            </span>
          )}
        </div>

        {/* Issuer */}
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontFamily: TYPOGRAPHY.mono.family,
          color: NEUTRAL.text300,
          marginTop: 2,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          issuer: {entry.issuerRef}
        </div>

        {/* Provenance / Artifact refs */}
        {(entry.provenanceRef || entry.artifactRef) && (
          <div style={{ display: 'flex', gap: SPACING.sp3, marginTop: SPACING.sp1, flexWrap: 'wrap' }}>
            {entry.provenanceRef && (
              <span style={{
                fontSize: TYPOGRAPHY.micro.size,
                fontFamily: TYPOGRAPHY.mono.family,
                color: NEUTRAL.text300,
              }}>
                prov: {entry.provenanceRef.slice(0, 12)}…
              </span>
            )}
            {entry.artifactRef && (
              <span style={{
                fontSize: TYPOGRAPHY.micro.size,
                fontFamily: TYPOGRAPHY.mono.family,
                color: NEUTRAL.text300,
              }}>
                artifact: {entry.artifactRef.slice(0, 12)}…
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export const WorkspaceReceiptPane: React.FC<WorkspaceReceiptPaneProps> = ({ model, onSelectReceipt }) => {
  if (model.error) {
    return (
      <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, fontSize: TYPOGRAPHY.body.size }}>
        Receipt pane error: {model.error}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
        borderBottom: `1px solid ${NEUTRAL.border}`,
        backgroundColor: NEUTRAL.bg600,
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: TYPOGRAPHY.heading.size,
          fontWeight: TYPOGRAPHY.heading.weight,
          color: NEUTRAL.text600,
        }}>
          Receipts
        </span>
        <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
          {model.verifiedCount} verified · {model.unverifiedCount} unverified
          {model.hashMismatchCount > 0 && (
            <span style={{ color: SEMANTIC.refusal.text, marginLeft: 4 }}>
              · {model.hashMismatchCount} mismatch
            </span>
          )}
        </span>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: SPACING.sp3 }}>

        {/* Safety strip */}
        <div style={{
          display: 'flex',
          gap: SPACING.sp2,
          flexWrap: 'wrap',
          marginBottom: SPACING.sp3,
          paddingBottom: SPACING.sp2,
          borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
        }}>
          {['READ-ONLY', 'PROOF-ONLY', 'NO EXECUTION AUTHORITY'].map(label => (
            <span key={label} style={{
              fontSize: TYPOGRAPHY.micro.size,
              fontWeight: 600,
              color: SEMANTIC.stabilized.text,
              backgroundColor: SEMANTIC.stabilized.chip,
              padding: '2px 6px',
              borderRadius: 3,
              textTransform: 'uppercase' as const,
              letterSpacing: 0.3,
            }}>
              {label}
            </span>
          ))}
        </div>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: SPACING.sp2,
          marginBottom: SPACING.sp3,
        }}>
          {[
            { label: 'Total', value: model.totalEntries, color: NEUTRAL.text500 },
            { label: 'Verified', value: model.verifiedCount, color: SEMANTIC.stabilized.text },
            { label: 'Unverified', value: model.unverifiedCount, color: NEUTRAL.text400 },
            { label: 'Mismatch', value: model.hashMismatchCount, color: model.hashMismatchCount > 0 ? SEMANTIC.refusal.text : NEUTRAL.text400 },
          ].map(stat => (
            <div key={stat.label} style={{
              backgroundColor: NEUTRAL.bg600,
              border: `1px solid ${NEUTRAL.borderSubtle}`,
              borderRadius: 2,
              padding: `${SPACING.sp2}px`,
              textAlign: 'center' as const,
            }}>
              <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text400, marginBottom: 2 }}>{stat.label}</div>
              <div style={{ fontSize: TYPOGRAPHY.label.size, fontWeight: 600, color: stat.color }}>{stat.value}</div>
            </div>
          ))}
        </div>

        {/* Hash mismatch warning */}
        {model.hashMismatchCount > 0 && (
          <div style={{
            padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
            backgroundColor: SEMANTIC.refusal.chip,
            border: `1px solid ${SEMANTIC.refusal.border}`,
            borderRadius: 2,
            marginBottom: SPACING.sp3,
            fontSize: TYPOGRAPHY.micro.size,
            color: SEMANTIC.refusal.text,
            fontWeight: 600,
          }}>
            {model.hashMismatchCount} receipt(s) have hash mismatches. Human review required.
          </div>
        )}

        {/* Receipt list */}
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          textTransform: 'uppercase' as const,
          letterSpacing: 0.4,
          marginBottom: SPACING.sp2,
        }}>
          Receipt Entries ({model.entries.length})
        </div>

        {model.entries.length === 0 ? (
          <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, textAlign: 'center' as const }}>
            No receipts
          </div>
        ) : (
          <div>
            {model.entries.map(entry => (
              <ReceiptRow key={entry.receiptId} entry={entry} onSelect={onSelectReceipt} />
            ))}
          </div>
        )}

      </div>
    </div>
  );
};
